<script lang="ts" setup>
import { api } from '@/api';
import { type BulkActionResultDtoOfString, type DatabaseBackupResult } from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import PtwBackButton from '@/components/buttons/PtwBackButton.vue';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import ActionConfirmationModal from '@/components/confirmationModals/ActionConfirmationModal.vue';
import { useActionWithConfirmation } from '@/components/confirmationModals/useActionWithConfirmation';
import Loader from '@/components/Loader/Loader.vue';
import Notification from '@/components/notifications/Notification.vue';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { truthy } from '@/utils/helper';
import { useDatabaseSettings } from '@/utils/useDatabaseSettings';
import BackupResult from '@/views/BackupView/components/BackupResult.vue';
import { useTranslation } from 'i18next-vue';
import { computed, ref } from 'vue';

const { t } = useTranslation();

const { isCustomDatabaseUsed, loading } = useDatabaseSettings({
  autoLoad: true,
});

const backupResults = ref<DatabaseBackupResult[]>([]);
const deleteResults = ref<BulkActionResultDtoOfString | null>(null);

const allBackupFilesSuccessfullyWritten = computed(
  () =>
    truthy(backupResults.value.length) &&
    backupResults.value.every((backupResult) => truthy(backupResult.success)),
);

// delete backup files
const { action: deleteBackupFiles, loading: isDeletingBackups } = useActionWithConfirmation({
  confirmedAction: async () => {
    const { success, result } = await api.installApi.deleteBackupFiles();
    if (success && result) {
      deleteResults.value = result;
      // clear backup results after successful deletion
      backupResults.value = [];
    }
    return result;
  },
});

// create backup files
const { action: createBackupWithConfirmation, loading: isCreatingBackup } =
  useActionWithConfirmation({
    confirmedAction: async () => {
      const { success, result } = await api.installApi.createBackup();
      if (success && result) {
        backupResults.value = result;
        // clear any previous delete results when creating new backup
        deleteResults.value = null;
      }
      return result;
    },
  });

// get backup file names
const getBackupFileNames = async (): Promise<string> => {
  const { success, result } = await api.installApi.getBackupFiles();
  if (success && result && result.length > 0) {
    return result.join('\n');
  }
  return t('NoBackupFilesFound');
};

const handleDeleteBackups = async () => {
  const backupFileNames = await getBackupFileNames();

  await deleteBackupFiles({
    param: undefined,
    confirmationOptions: {
      title: t('DeleteBackupFiles'),
      text: `${t('DeleteBackupFilesConfirmation')}\n\n${t('BackupFiles')}:\n${backupFileNames}`,
      confirmButton: t('Delete'),
      confirmationButtonType: 'Delete',
    },
  });
};

const handleCreateBackup = async () => {
  await createBackupWithConfirmation({
    param: undefined,
    confirmationOptions: {
      title: t('CreateDatabaseBackup'),
      text: t('CreateBackupConfirmation'),
      confirmButton: t('Backup'),
      confirmationButtonType: 'Save',
    },
  });
};
</script>

<template>
  <ActionConfirmationModal>
    <BasePage>
      <template #title>
        <BasePageTitle :title="$t('Backup')" />
      </template>
      <template #actions>
        <template v-if="!loading">
          <PtwBackButton v-if="isCustomDatabaseUsed" :to="{ name: routenames.settings }" />
          <template v-else>
            <PtwCancelButton :to="{ name: routenames.settings }" />
            <Button
              variant="special"
              :label="$t('DeleteBackupFiles')"
              :loading="isDeletingBackups"
              class="ml-2"
              @click="handleDeleteBackups"
            />
            <Button
              variant="primary"
              :label="$t('Backup')"
              :loading="isCreatingBackup"
              class="ml-4"
              @click="handleCreateBackup"
            />
          </template>
        </template>
      </template>

      <v-sheet class="pa-4">
        <div class="text-body-1">
          <h4 class="mb-2">
            {{ $t('DatabaseBackup') }}
          </h4>
          <Loader v-if="loading" type="content" />
          <p v-else class="text-pre-line">
            {{ isCustomDatabaseUsed ? $t('CustomDatabaseUsedMsg') : $t('CreateABackupMsg') }}
          </p>
        </div>

        <Notification
          v-if="!isCustomDatabaseUsed"
          class="mt-3 w-lg-33"
          :closable="false"
          :value="{
            type: 'warning',
            message: $t('BackupRestoreWarningMsg'),
          }"
        />

        <!-- Success notification for backup creation -->
        <Notification
          v-if="allBackupFilesSuccessfullyWritten"
          :value="{
            type: 'success',
            closable: false,
            message: $t('TransferBackupInstructionsMsg'),
          }"
          class="mt-4"
        />

        <!-- Success notification for backup deletion -->
        <Notification
          v-if="
            deleteResults &&
            deleteResults.itemsActionSucceeded &&
            deleteResults.itemsActionSucceeded.length > 0
          "
          :value="{
            type: 'success',
            closable: true,
            message: $t('BackupFilesDeletedSuccessfully', {
              count: deleteResults.itemsActionSucceeded.length,
            }),
          }"
          class="mt-4"
        />

        <!-- Error notification for backup deletion -->
        <Notification
          v-if="
            deleteResults &&
            deleteResults.itemsActionFailed &&
            deleteResults.itemsActionFailed.length > 0
          "
          :value="{
            type: 'error',
            closable: true,
            message: $t('BackupFilesDeletionFailed', {
              count: deleteResults.itemsActionFailed.length,
            }),
          }"
          class="mt-4"
        />

        <div class="mt-4">
          <BackupResult
            v-for="(backupResult, index) in backupResults"
            :key="index"
            :value="backupResult"
            :fileNameOnly="allBackupFilesSuccessfullyWritten"
          />
        </div>
      </v-sheet>
    </BasePage>
  </ActionConfirmationModal>
</template>
